// D3 Hexbin Chart for BodyOS data visualizations
// Shows density/correlation between two variables (sleep vs performance, etc.)
import * as d3 from 'd3'
import { hexbin as d3Hexbin } from 'd3-hexbin'
import {
  colors,
  fonts,
  animationConfig,
  defaultMargins,
  defaultDimensions
} from './config'
import {
  createSvg,
  createTooltip,
  showTooltipHtml,
  hideTooltip,
  debounce
} from './utils'

export default class HexbinChart {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' ? document.querySelector(container) : container
    this.options = {
      width: defaultDimensions.width,
      height: defaultDimensions.height,
      margins: { ...defaultMargins },
      xLabel: '',
      yLabel: '',
      hexRadius: 20,
      colorScheme: 'grayscale', // 'grayscale', 'sequential', or custom
      showLegend: true,
      animate: true,
      responsive: true,
      ...options
    }

    this.data = []
    this.svg = null
    this.tooltip = null

    if (this.options.responsive) {
      this.handleResize = debounce(() => this.render(), 250)
      window.addEventListener('resize', this.handleResize)
    }
  }

  setData(data) {
    this.data = data
    return this
  }

  render() {
    this.container.innerHTML = ''

    if (!this.data || this.data.length === 0) {
      this.renderEmptyState()
      return this
    }

    const containerWidth = this.container.clientWidth || this.options.width
    const width = Math.min(containerWidth, this.options.width)
    const height = (width / this.options.width) * this.options.height

    const { svg, g, innerWidth, innerHeight } = createSvg(
      this.container,
      width,
      height,
      this.options.margins
    )
    this.svg = svg

    this.tooltip = createTooltip(this.container)

    // X scale
    const xExtent = d3.extent(this.data, (d) => d.x)
    const xScale = d3.scaleLinear()
      .domain([xExtent[0] * 0.95, xExtent[1] * 1.05])
      .range([0, innerWidth])

    // Y scale
    const yExtent = d3.extent(this.data, (d) => d.y)
    const yScale = d3.scaleLinear()
      .domain([yExtent[0] * 0.95, yExtent[1] * 1.05])
      .range([innerHeight, 0])

    // Create hexbin generator
    const hexRadius = Math.min(
      this.options.hexRadius,
      Math.min(innerWidth, innerHeight) / 10
    )

    const hexbin = d3Hexbin()
      .x((d) => xScale(d.x))
      .y((d) => yScale(d.y))
      .radius(hexRadius)
      .extent([[0, 0], [innerWidth, innerHeight]])

    const hexbins = hexbin(this.data)

    // Color scale based on count
    const maxCount = d3.max(hexbins, (d) => d.length)
    const colorScale = this.getColorScale(maxCount)

    // Draw axes
    this.drawAxes(g, xScale, yScale, innerWidth, innerHeight)

    // Draw hexagons
    const hexagons = g.selectAll('.hexagon')
      .data(hexbins)
      .enter()
      .append('path')
      .attr('class', 'hexagon')
      .attr('d', hexbin.hexagon())
      .attr('transform', (d) => `translate(${d.x}, ${d.y})`)
      .attr('fill', (d) => colorScale(d.length))
      .attr('stroke', colors.neutral[900])
      .attr('stroke-width', 1)
      .style('cursor', 'pointer')

    if (this.options.animate) {
      hexagons
        .attr('opacity', 0)
        .attr('transform', (d) => `translate(${d.x}, ${d.y}) scale(0)`)
        .transition()
        .duration(animationConfig.duration)
        .delay((d, i) => i * 10)
        .attr('opacity', 0.9)
        .attr('transform', (d) => `translate(${d.x}, ${d.y}) scale(1)`)
    } else {
      hexagons.attr('opacity', 0.9)
    }

    // Add interactions
    this.addHexInteractions(hexagons)

    // Legend
    if (this.options.showLegend) {
      this.renderLegend(g, colorScale, maxCount, innerWidth)
    }

    return this
  }

  getColorScale(maxCount) {
    switch (this.options.colorScheme) {
      case 'sequential':
        return d3.scaleSequential()
          .domain([0, maxCount])
          .interpolator(d3.interpolateBlues)

      case 'grayscale':
      default:
        return d3.scaleLinear()
          .domain([0, maxCount / 2, maxCount])
          .range([colors.neutral[100], colors.neutral[500], colors.neutral[900]])
    }
  }

  drawAxes(g, xScale, yScale, innerWidth, innerHeight) {
    // X axis
    const xAxis = g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${innerHeight})`)
      .call(d3.axisBottom(xScale).ticks(8))

    xAxis.selectAll('text')
      .attr('fill', colors.textMuted)
      .attr('font-size', fonts.sizes.sm)
      .attr('font-family', fonts.family)

    xAxis.selectAll('line, path')
      .attr('stroke', colors.axis)
      .attr('stroke-width', 2)

    if (this.options.xLabel) {
      g.append('text')
        .attr('x', innerWidth / 2)
        .attr('y', innerHeight + 45)
        .attr('text-anchor', 'middle')
        .attr('fill', colors.text)
        .attr('font-size', fonts.sizes.base)
        .attr('font-weight', fonts.weights.medium)
        .text(this.options.xLabel.toUpperCase())
    }

    // Y axis
    const yAxis = g.append('g')
      .attr('class', 'y-axis')
      .call(d3.axisLeft(yScale).ticks(8))

    yAxis.selectAll('text')
      .attr('fill', colors.textMuted)
      .attr('font-size', fonts.sizes.sm)
      .attr('font-family', fonts.family)

    yAxis.selectAll('line, path')
      .attr('stroke', colors.axis)
      .attr('stroke-width', 2)

    if (this.options.yLabel) {
      g.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -innerHeight / 2)
        .attr('y', -45)
        .attr('text-anchor', 'middle')
        .attr('fill', colors.text)
        .attr('font-size', fonts.sizes.base)
        .attr('font-weight', fonts.weights.medium)
        .text(this.options.yLabel.toUpperCase())
    }

    // Grid lines
    g.append('g')
      .attr('class', 'grid-x')
      .attr('transform', `translate(0, ${innerHeight})`)
      .call(d3.axisBottom(xScale)
        .ticks(8)
        .tickSize(-innerHeight)
        .tickFormat(''))
      .selectAll('line')
      .attr('stroke', colors.grid)
      .attr('stroke-width', 1)

    g.selectAll('.grid-x path').remove()

    g.append('g')
      .attr('class', 'grid-y')
      .call(d3.axisLeft(yScale)
        .ticks(8)
        .tickSize(-innerWidth)
        .tickFormat(''))
      .selectAll('line')
      .attr('stroke', colors.grid)
      .attr('stroke-width', 1)

    g.selectAll('.grid-y path').remove()
  }

  addHexInteractions(hexagons) {
    const self = this

    hexagons
      .on('mouseover', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('opacity', 1)
          .attr('stroke-width', 2)

        // Calculate average values in this hexbin
        const avgX = d3.mean(d, (p) => p.x)
        const avgY = d3.mean(d, (p) => p.y)

        const formatX = self.options.formatX || ((v) => v.toFixed(1))
        const formatY = self.options.formatY || ((v) => v.toFixed(1))

        const content = `
          <strong>${d.length} data points</strong><br/>
          <span style="color: ${colors.textMuted}">Avg ${self.options.xLabel || 'X'}: </span>
          <span style="color: ${colors.accent}">${formatX(avgX)}</span><br/>
          <span style="color: ${colors.textMuted}">Avg ${self.options.yLabel || 'Y'}: </span>
          <span style="color: ${colors.accent}">${formatY(avgY)}</span>
        `
        showTooltipHtml(self.tooltip, content, event)
      })
      .on('mouseout', function() {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('opacity', 0.9)
          .attr('stroke-width', 1)
        hideTooltip(self.tooltip)
      })
  }

  renderLegend(g, colorScale, maxCount, innerWidth) {
    const legendWidth = 150
    const legendHeight = 12
    const legendX = innerWidth - legendWidth
    const legendY = -20

    // Gradient
    const defs = this.svg.select('defs').empty() ?
      this.svg.append('defs') :
      this.svg.select('defs')

    const gradientId = 'hexbin-legend-gradient'
    const gradient = defs.append('linearGradient')
      .attr('id', gradientId)
      .attr('x1', '0%')
      .attr('x2', '100%')

    const steps = 10
    for (let i = 0; i <= steps; i += 1) {
      const t = i / steps
      gradient.append('stop')
        .attr('offset', `${t * 100}%`)
        .attr('stop-color', colorScale(t * maxCount))
    }

    const legend = g.append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${legendX}, ${legendY})`)

    legend.append('rect')
      .attr('width', legendWidth)
      .attr('height', legendHeight)
      .attr('fill', `url(#${gradientId})`)
      .attr('stroke', colors.neutral[900])
      .attr('stroke-width', 1)

    legend.append('text')
      .attr('x', 0)
      .attr('y', legendHeight + 14)
      .attr('fill', colors.textMuted)
      .attr('font-size', fonts.sizes.xs)
      .attr('font-family', fonts.family)
      .text('0')

    legend.append('text')
      .attr('x', legendWidth)
      .attr('y', legendHeight + 14)
      .attr('text-anchor', 'end')
      .attr('fill', colors.textMuted)
      .attr('font-size', fonts.sizes.xs)
      .attr('font-family', fonts.family)
      .text(maxCount)

    legend.append('text')
      .attr('x', legendWidth / 2)
      .attr('y', -5)
      .attr('text-anchor', 'middle')
      .attr('fill', colors.textMuted)
      .attr('font-size', fonts.sizes.xs)
      .attr('font-family', fonts.family)
      .text('DENSITY')
  }

  renderEmptyState() {
    const emptyDiv = document.createElement('div')
    emptyDiv.className = 'd3-empty-state'
    emptyDiv.innerHTML = `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        height: 200px;
        border: 2px dashed ${colors.neutral[300]};
        color: ${colors.textMuted};
        font-family: ${fonts.family};
        font-size: ${fonts.sizes.base}px;
      ">
        NO DATA AVAILABLE
      </div>
    `
    this.container.appendChild(emptyDiv)
  }

  destroy() {
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
    }
    if (this.container) {
      this.container.innerHTML = ''
    }
  }
}
